import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { ExternalLink, Mail, Search, SearchX, Video } from 'lucide-react'
import { Badge, Button, ButtonLink, Card, EmptyState, Notice, TextField } from '@/components/play'
import '@/components/onboarding/onboarding.css'
import { loadOnboardingPayload, type OnboardingPayload } from '@/lib/onboardingPayload'
import { OnboardingCameraCard } from '@/components/onboarding/OnboardingCameraCard'
import { SitePage } from '@/components/SitePage'

const APP_STORE_BADGE_URL =
  'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83'
const SUPPORT_EMAIL = 'comrades@streambridge.live'

type StepLink = {
  href: string
  label: string
  kind: 'app-store'
}

type StepAction = {
  href: string
  label: string
  primary?: boolean
}

type SupportKind = 'discord' | 'whatsapp'

const PREVIEW_PIN_POSITIONS = [
  { left: '18%', top: '72%' },
  { left: '40%', top: '48%' },
  { left: '62%', top: '56%' },
  { left: '78%', top: '28%' },
]

/** Splits editable copy into paragraphs, preserving author line breaks. */
function Prose({ text, className }: { text: string; className?: string }) {
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
  return (
    <div className={className}>
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          {p}
        </p>
      ))}
    </div>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-size-xs font-medium text-neutral-7">{children}</span>
  )
}

function BrandIcon({ kind, className }: { kind: SupportKind; className?: string }) {
  const path =
    kind === 'discord'
      ? 'M20.317 4.37a19.8 19.8 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 13.4 13.4 0 0 0-.608 1.25 18.3 18.3 0 0 0-5.487 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.7 19.7 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.1 14.1 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.25-.192.372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.122.1.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.55-13.66a.061.061 0 0 0-.03-.03ZM8.02 15.33c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.946 2.418-2.157 2.418Z'
      : 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.58-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.946L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z'

  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={path} />
    </svg>
  )
}

function SupportLink({
  href,
  label,
  kind,
  primary,
}: {
  href: string
  label: string
  kind: SupportKind
  primary?: boolean
}) {
  return (
    <ButtonLink
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={`Open ${label} help`}
      variant={primary ? 'primary' : 'secondary'}
    >
      <BrandIcon kind={kind} />
      {label}
    </ButtonLink>
  )
}

function InstallLinks({ links }: { links: StepLink[] }) {
  const appStoreLink = links.find((link) => link.kind === 'app-store')

  if (!appStoreLink) return null

  return (
    <div className="mt-4">
      {/* Apple's badge, as supplied; the link keeps a 44px target and Play's focus ring (onboarding.css). */}
      <a
        href={appStoreLink.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download StreamBridge on the App Store"
        className="play-app-store-badge"
      >
        <img src={APP_STORE_BADGE_URL} alt="" width={120} height={40} loading="lazy" />
      </a>
    </div>
  )
}

function StepActionLink({ action }: { action: StepAction }) {
  return (
    <ButtonLink
      href={action.href}
      target="_blank"
      rel="noopener noreferrer"
      variant={action.primary ? 'primary' : 'secondary'}
      className={action.primary ? 'mt-4 w-full sm:w-auto' : 'mt-4 w-fit'}
    >
      {action.label}
      <ExternalLink aria-hidden="true" />
    </ButtonLink>
  )
}

function EventPagePreview({
  eventName,
  viewerUrl,
  cameras,
}: {
  eventName: string
  viewerUrl: string
  cameras: OnboardingPayload['cameras']
}) {
  const previewCameras = (cameras.length > 0 ? cameras : [{ name: 'Course camera' }]).slice(0, 4)
  const broadcastPrompt = 'Mr Price Umlaas'

  return (
    <Card
      as="section"
      className="mt-8"
      title={`${eventName} companion page`}
      description="Where your sponsor cameras appear on race day."
      actions={
        <ButtonLink
          href={viewerUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
          size="compact"
        >
          Visit the Comrades page
          <ExternalLink aria-hidden="true" />
        </ButtonLink>
      }
    >
      <div className="grid gap-4">
        <div className="relative min-h-[220px] overflow-hidden rounded-xl bg-neutral-1 sm:min-h-[260px]">
          <div className="absolute left-4 top-4">
            <Badge tone="overlay">Course map</Badge>
          </div>
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 320 220" fill="none" aria-hidden="true">
            {/* The course is ink, as on the event map; green stays for live. */}
            <path
              d="M44 168 C88 122 111 115 131 80 C150 47 204 75 222 50 C239 27 260 31 281 42"
              className="stroke-neutral-10"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
          {previewCameras.map((camera, index) => {
            const position = PREVIEW_PIN_POSITIONS[index] ?? PREVIEW_PIN_POSITIONS[0]
            return (
              <div
                key={`${camera.name}-${index}`}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: position.left, top: position.top }}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-10 text-xs font-medium tabular-nums text-neutral-1">
                  {index + 1}
                </span>
                <span className="mt-1 block max-w-[92px] truncate rounded-full bg-neutral-3 px-2 py-0.5 text-size-xxs font-medium text-neutral-10">
                  {camera.name}
                </span>
              </div>
            )
          })}
        </div>

        <div className="flex min-h-[220px] flex-col justify-between gap-3 rounded-xl bg-neutral-1 p-3">
          <h3 className="px-1 text-sm font-semibold text-neutral-10">
            Integrated into the broadcast
          </h3>
          {/* An illustration of the broadcast's QR, labelled as one: no picture, and no live state without a camera. */}
          <div className="relative aspect-video overflow-hidden rounded-lg bg-neutral-10">
            <div className="absolute left-3 top-3 z-10">
              <Badge tone="overlay">Example</Badge>
            </div>
            <div className="absolute bottom-4 left-4 right-[92px] top-[58px] rounded-md bg-neutral-9" />
            <div className="absolute inset-x-4 bottom-4 z-10 flex items-end justify-between gap-3">
              <div className="min-w-0 rounded-md bg-neutral-8 px-3 py-2">
                <span className="block text-size-xxs font-medium text-neutral-4">
                  Scan to watch
                </span>
                <span className="mt-0.5 block truncate text-xs font-medium text-neutral-1">{broadcastPrompt}</span>
              </div>
              <div className="shrink-0 rounded-md bg-neutral-1 p-1.5">
                <QRCodeSVG value={viewerUrl} size={48} level="M" />
              </div>
            </div>
          </div>
          <p className="text-sm text-neutral-7">
            Athletes, families and fans find your sponsor cameras on the companion page map, or by scanning the QR
            code on the broadcast.
          </p>
        </div>
      </div>
    </Card>
  )
}

export function OnboardingPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const [payload, setPayload] = useState<OnboardingPayload | null>(null)
  const [error, setError] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    loadOnboardingPayload(eventId)
      .then((p) => !cancelled && setPayload(p))
      .catch(() => !cancelled && setError(true))
    return () => {
      cancelled = true
    }
  }, [eventId])

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.streambridge.live'

  const viewerUrlFor = useMemo(
    () => (identity: string) => `${origin}/comrades/${eventId}?camera=${encodeURIComponent(identity)}`,
    [origin, eventId],
  )

  const cameras = payload?.cameras ?? []
  const filteredCameras = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return cameras
    return cameras.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.sponsor?.sponsor_name.toLowerCase().includes(q) ?? false),
    )
  }, [cameras, query])

  if (error) {
    return (
      <SitePage mainId="onboarding-main" mainClassName="flex min-h-[60vh] items-center justify-center px-6">
        <div className="w-full max-w-md">
          <h1 className="sr-only">Camera and sponsor onboarding</h1>
          <Notice tone="error" title="We couldn’t load this onboarding guide">
            Check the link and try again.
          </Notice>
        </div>
      </SitePage>
    )
  }

  if (!payload) {
    return (
      <SitePage mainId="onboarding-main" mainClassName="flex min-h-[60vh] items-center justify-center">
        <ContentSkeleton label="Loading onboarding guide" variant="viewer" className="max-w-3xl px-6 py-20" />
      </SitePage>
    )
  }

  const { event, onboarding } = payload
  const requirementsUrl = `${origin}/sponsors`
  const generalViewerUrl = `${origin}/comrades/${eventId}`

  const steps: { title: string; body: string; links: StepLink[]; action?: StepAction }[] = [
    {
      title: 'Send us your sponsor graphics',
      body: `Check the sponsor specifications, then send us your graphics to ${SUPPORT_EMAIL}.`,
      links: [],
      action: {
        href: requirementsUrl,
        label: 'Sponsor specifications',
      },
    },
    {
      title: 'Install the app',
      body: onboarding.iphone_requirements,
      links: [
        onboarding.app_store_url && { href: onboarding.app_store_url, label: 'App Store', kind: 'app-store' },
      ].filter(Boolean) as StepLink[],
    },
    { title: 'Open your camera invite and go live', body: `Ask your host for your camera’s invitation.\n\n${onboarding.activation}\n\n${onboarding.how_to_stream}`, links: [] },
    {
      title: 'Preview on the Comrades Map',
      body: onboarding.testing_intro,
      links: [],
      action: {
        href: generalViewerUrl,
        label: 'Preview your camera on the Comrades map',
        primary: true,
      },
    },
  ]

  return (
    <SitePage mainId="onboarding-main" className="text-neutral-10" mainClassName="mx-auto max-w-3xl px-6 py-20 sm:py-28">
      {/* ── Hero ───────────────────────────────────────────────── */}
      <header className="flex flex-col items-center text-center">
        {event.logo_url ? (
          <img
            src={event.logo_url}
            alt={`${event.name} logo`}
            className="mb-8 max-h-20 w-auto object-contain"
          />
        ) : null}
        <Eyebrow>Camera &amp; sponsor onboarding</Eyebrow>
        <h1 className="mt-4 text-4xl font-extrabold sm:text-6xl">
          {event.name}
        </h1>
        <Prose text={onboarding.intro} className="mt-6 max-w-xl space-y-4 text-lg text-neutral-7" />
      </header>

      {/* ── Why ────────────────────────────────────────────────── */}
      <section className="mt-20 border-l-2 border-neutral-10 pl-6">
        <Prose
          text={onboarding.why}
          className="space-y-3 text-xl font-medium text-neutral-10"
        />
      </section>

      <EventPagePreview eventName={event.name} viewerUrl={generalViewerUrl} cameras={cameras} />

      {/* ── 4-step path ────────────────────────────────────────── */}
      <section className="mt-20">
        <div className="text-center">
          <Eyebrow>Go live</Eyebrow>
          <h2 className="mt-3 text-3xl font-bold">Four steps to live</h2>
        </div>

        <ol className="mt-12 space-y-px">
          {steps.map((step, i) => (
            <li key={step.title} className="relative flex gap-5 sm:gap-7">
              {/* number + connecting line */}
              <div className="flex flex-col items-center">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-3 font-medium tabular-nums text-neutral-10">
                  {i + 1}
                </span>
                {i < steps.length - 1 ? <span className="w-px flex-1 bg-neutral-4" /> : null}
              </div>
              <div className="flex-1 pb-10">
                <h3 className="text-lg font-semibold text-neutral-10">{step.title}</h3>
                <Prose text={step.body} className="mt-2 space-y-2 text-sm text-neutral-7" />
                {step.links.length > 0 ? (
                  <InstallLinks links={step.links} />
                ) : null}
                {step.action ? <StepActionLink action={step.action} /> : null}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Help ───────────────────────────────────────────────── */}
      <section className="mt-16 flex flex-col items-center gap-5 border-t border-neutral-4 pt-14 text-center">
        <div>
          <Eyebrow>Need a hand?</Eyebrow>
          <p className="mt-3 text-lg text-neutral-7">We&apos;re right here. Come say hi.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {onboarding.discord_url ? (
            <SupportLink href={onboarding.discord_url} label="Discord" kind="discord" primary />
          ) : null}
          {onboarding.whatsapp_url ? (
            <SupportLink href={onboarding.whatsapp_url} label="WhatsApp" kind="whatsapp" />
          ) : null}
        </div>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-neutral-10 underline underline-offset-4 transition-colors hover:text-neutral-8"
        >
          <Mail size={15} aria-hidden="true" />
          {SUPPORT_EMAIL}
        </a>
      </section>

      {/* ── Cameras ────────────────────────────────────────────── */}
      <section className="mt-20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow>Find your camera</Eyebrow>
            <h2 className="mt-3 text-3xl font-bold">
              Sponsor cameras
              <span className="ml-2 text-xl font-normal tabular-nums text-neutral-7">{cameras.length}</span>
            </h2>
            <p className="mt-2 text-sm text-neutral-7">
              Find your sponsor camera to watch it. Your host will share a separate invitation to go live.
            </p>
          </div>
          {cameras.length > 4 ? (
            <TextField
              label="Search sponsor cameras"
              hideLabel
              icon={<Search />}
              className="w-full sm:w-64"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sponsor cameras"
            />
          ) : null}
        </div>

        {cameras.length === 0 ? (
          <EmptyState
            className="mt-8"
            headingLevel={3}
            icon={<Video />}
            title="No cameras yet"
            description="They’ll appear here once the organiser adds them."
          />
        ) : filteredCameras.length === 0 ? (
          <EmptyState
            className="mt-8"
            headingLevel={3}
            icon={<SearchX />}
            title="No matching cameras"
            description={`Nothing matches “${query}”. Check the spelling or clear the search.`}
            action={<Button variant="secondary" onClick={() => setQuery('')}>Clear search</Button>}
          />
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {filteredCameras.map((camera) => (
              <OnboardingCameraCard
                key={camera.stream_id}
                camera={camera}
                viewerUrl={viewerUrlFor(camera.participant_identity)}
                requirementsUrl={requirementsUrl}
              />
            ))}
          </div>
        )}
      </section>
    </SitePage>
  )
}
